import { Card, toast } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { logger } from "@bfc-software/app-toolbox";

import {
  useSystemSettings,
  useUpdateSystemSettings,
  type SystemSettings,
} from "../-data/warehouse-settings-client";
import { StatStepper } from "./stat-stepper";
import styles from "./system-settings-card.module.css";

export function SystemSettingsCard({
  orgId,
  warehouseId,
  t,
}: {
  orgId: string;
  warehouseId: string | null;
  t: TFunction;
}) {
  const query = useSystemSettings(orgId, warehouseId);
  const update = useUpdateSystemSettings(orgId, warehouseId);

  const set = (field: keyof SystemSettings, value: number) => {
    update.mutate(
      { [field]: value },
      {
        onSuccess: () =>
          logger.info("Updated system settings", { field, value }),
        onError: (error) => {
          logger.warn("Failed to update system settings", { error, field });
          toast.error(t("systemControls.systemSettings.saveFailed"));
        },
      },
    );
  };

  const settings = query.data;

  return (
    <Card
      variant="panel"
      layout="body-only"
      data-test-role="system-settings-card"
      body={
        <>
          <div className={styles.title}>
            {t("systemControls.systemSettings.title")}
          </div>
          <div className={styles.subtitle}>
            {t("systemControls.systemSettings.subtitle")}
          </div>

          {settings ? (
            <div className={styles.rows}>
              <StatStepper
                data-test-role="validation-accuracy-threshold"
                label={t(
                  "systemControls.systemSettings.validationAccuracyThreshold.label",
                )}
                description={t(
                  "systemControls.systemSettings.validationAccuracyThreshold.description",
                )}
                value={settings.validationAccuracyThresholdPercent}
                unit={t("systemControls.systemSettings.percentUnit")}
                min={50}
                max={100}
                isDisabled={update.isPending}
                onChange={(next) =>
                  set("validationAccuracyThresholdPercent", next)
                }
              />
              <StatStepper
                data-test-role="observation-window-duration"
                label={t(
                  "systemControls.systemSettings.observationWindowDuration.label",
                )}
                description={t(
                  "systemControls.systemSettings.observationWindowDuration.description",
                )}
                value={settings.observationWindowDurationDays}
                unit={t("systemControls.systemSettings.daysUnit")}
                min={1}
                max={90}
                isDisabled={update.isPending}
                onChange={(next) => set("observationWindowDurationDays", next)}
              />
              <StatStepper
                data-test-role="confidence-gate-scan-count"
                label={t(
                  "systemControls.systemSettings.confidenceGateScanCount.label",
                )}
                description={t(
                  "systemControls.systemSettings.confidenceGateScanCount.description",
                )}
                value={settings.confidenceGateScanCount}
                unit={t("systemControls.systemSettings.scansUnit")}
                min={1}
                max={10}
                isDisabled={update.isPending}
                onChange={(next) => set("confidenceGateScanCount", next)}
              />
            </div>
          ) : null}
        </>
      }
    />
  );
}
